import get from 'lodash/get';
import React from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {FormattedMessage} from 'react-intl';
import {Grid, Header, Menu, Segment} from 'semantic-ui-react';
import includes from 'lodash/includes';
import capitalize from 'lodash/capitalize';
import compose from 'recompose/compose';
import lifecycle from 'recompose/lifecycle';
import withProps from 'recompose/withProps';
import setDisplayName from 'recompose/setDisplayName';
import * as ResourceApi from 'api/resources';
import {startDeleteAction} from 'pages/DeleteConfirmation/actions';
import {getUserGroupName, getUserServiceActions} from 'pages/Profile/selectors';
import {UserGroups} from 'utils/DBUtils';
import UserDetailNav from './UserDetailNav';
import UserSearchPanel from '../UserSearchPanel/UserSearchPanel';
import UserDetailActionsButton from './UserDetailActionsButton';
import {
    AwardDaysModalContainer,
    AwardPointsModalContainer,
    AwardSpinsModalContainer,
    ChangeEmailModalContainer,
    ChangePasswordModalContainer,
    ConfirmDeleteUserDataModal,
    DeleteUserModalContainer,
    RepairProfileModalContainer,
    RevokeAmountOfPointsModalContainer,
    RevokePointsModalContainer
} from './modals';
import {
    fetchUserAction,
    startAwardDaysAction,
    startAwardPointsAction,
    startAwardSpinsAction,
    startChangeEmailAction,
    startChangePasswordAction,
    startDeleteUserDataAction,
    startRepairProfileAction,
    startRevokeAmountOfPointsAction,
    startRevokePointsAction
} from '../actions';
import {isNoU} from "../../../utils/DBUtils";
import {clearResourcePageAction} from "../../../resources/actions";

const getPermittedActions = getUserServiceActions(ResourceApi.Types.User);

const mapStateToProps = state => {

  const {
    currentUser,
    currentUserId,
    loadingUserProfile,
    failedUserProfile,
    loadingStartsCompletes,
    failedStartsCompletes,
    failedRaffleEntries,
    loadingRaffleEntries,
    loadingPrizeboothOrders,
    failedPrizeboothOrders,
    loadingPointHistory,
    failedPointHistory,
  } = state.userManagement;

  const permittedActions = getPermittedActions(state);

  const allowAwardPoints = includes(permittedActions, 'AWARD_POINTS');
  const allowAwardSpins = includes(permittedActions, 'AWARD_SPINS');
  const allowAwardDays = includes(permittedActions, 'AWARD_CONSECUTIVE_DAYS');
  const allowChangeEmail = includes(permittedActions, 'CHANGE_PASSWORD');   // <-- minor hack
  const allowChangePassword = includes(permittedActions, 'CHANGE_PASSWORD');
  const allowRepairProfile = includes(permittedActions, 'REPAIR_PROFILE');
  const allowRevokePoints = includes(permittedActions, 'REVOKE_POINTS');
  const allowDeactivateUser = includes(permittedActions, 'DEACTIVATE_USER');
  const allowDeleteUserData = includes(permittedActions, 'DELETE_USER_DATA');
  const allowCancelPoints = includes(permittedActions, 'CANCEL_TRANSACTION');

  const userGroupName = capitalize(getUserGroupName(state));

  return {
    user: currentUser,
    currentUserId,
    userGroupName,

    loggedInUser: state.userProfile.profile,

    loadingUserProfile,
    failedUserProfile,
    loadingStartsCompletes,
    failedStartsCompletes,
    loadingRaffleEntries,
    failedRaffleEntries,
    loadingPrizeboothOrders,
    failedPrizeboothOrders,
    loadingPointHistory,
    failedPointHistory,

    allowAwardPoints,
    allowAwardSpins,
    allowAwardDays,
    allowChangeEmail,
    allowChangePassword,
    allowRepairProfile,
    allowRevokePoints,
    allowDeactivateUser,
    allowDeleteUserData,
    allowCancelPoints,
  };
};

const mapDispatchToProps = dispatch => bindActionCreators({
  fetchUser: fetchUserAction,
  awardPoints: startAwardPointsAction,
  awardSpins: startAwardSpinsAction,
  awardDays: startAwardDaysAction,
  changeEmail: startChangeEmailAction,
  changePassword: startChangePasswordAction,
  repairProfile: startRepairProfileAction,
  revokePoints: startRevokePointsAction,
  clearStoreOrdersPage: () => clearResourcePageAction(ResourceApi.Types.PrizeboothOrder),
  revokeAmountOfPoints: startRevokeAmountOfPointsAction,
  deleteUserData: (user) => startDeleteUserDataAction(user),
  deleteOrUndeleteUser: user => startDeleteAction(
    user,
    user.resourceId,
    [], // additional resource ids
    {}, // additional data
    []  // post actions
  )
}, dispatch);

const UserDetailPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  lifecycle({
    componentWillMount() {
      // TODO: get this to work properly with ReactRouter params
      if (this.props.match.params[0]) {
          const param = this.props.match.params[0];
          // can start with a '/'
          // if just use split, first element is an empty string
          // replace '/' with space and them trim to remove any leading or trailing '/'
          const params = param.replace(/\//g, " ").trim().split(" ");
          const userId = params[0];
          if (!isNoU(userId) && this.props.currentUserId !== userId) {
              this.props.fetchUser(userId);
          }
      }
    },
    componentWillUnmount() {
        this.props.clearStoreOrdersPage();
    }
  }),
  withProps(props => ({
    closeOnClick: () => {
      props.history.push('/users');
    },

    ...{
      // pass false if the authenticted user lacks permissions for any of these actions;
      // the actions menu dropdown will hide any options lacking an action handler.
      awardPoints: props.allowAwardPoints && props.awardPoints,
      awardSpins: props.allowAwardSpins && props.awardSpins,
      awardDays: props.allowAwardDays && props.awardDays,
      changeEmail: props.allowChangeEmail && props.changeEmail,
      changePassword: props.allowChangePassword && props.changePassword,
      revokeAmountOfPoints: props.allowRevokePoints && props.revokeAmountOfPoints,
      repairProfile: props.allowRepairProfile && props.repairProfile,
      deleteUserData: props.allowDeleteUserData && props.deleteUserData,
      deleteOrUndeleteUser: props.allowDeactivateUser && props.deleteOrUndeleteUser
    }
  })),
  setDisplayName('UserDetailPage')
)(props => (
  <div className='user-management user-detail'>
      {props.hideTitle === true ? "" : (
        <Header as='h1'>
          <FormattedMessage id='userManagement.title' />
        </Header>
      )}
    <br/>
    <Grid columns={2}>
      <Grid.Column width={4}>
        <Segment>
          <Header as='h3'>
            <FormattedMessage id='userManagement.searchPanel.header' />
          </Header>
          <UserSearchPanel
            redirectUrl={props.listUsersPath}
            buildFilters={props.buildFilters}
            formConfig={props.searchFormConfig}
            filterResource={props.filterResource}
            searchButtonKey={props.searchButtonKey}
            showExportButton={props.searchFormExport} />
        </Segment>
      </Grid.Column>
      <Grid.Column width={12}>
        <Segment>
          <Grid columns={2}>
            <Grid.Column>
              <Header as='h3'>
                #{props.currentUserId}
                {props.user && props.user.id === props.currentUserId && (
                  `/ ${props.user.firstName || ''} ${props.user.lastName || ''}`
                )}
              </Header>
            </Grid.Column>
            <Grid.Column textAlign='right' className='user-detail-actions'>
              <Menu.Item onClick={props.closeOnClick}>
                <FormattedMessage id='userManagement.userDetail.button.close' />
              </Menu.Item>
              {!(props.loadingUserProfile || props.failedUserProfile || isNoU(props.user)) && (
                <UserDetailActionsButton
                  user={props.user}
                  awardPoints={props.awardPoints}
                  awardSpins={props.awardSpins}
                  awardDays={props.awardDays}
                  changeEmail={props.changeEmail}
                  changePassword={props.changePassword}
                  revokeAmountOfPoints={props.revokeAmountOfPoints}
                  repairProfile={props.repairProfile}
                  deleteUserData={props.deleteUserData}
                  deleteOrUndeleteUser={props.deleteOrUndeleteUser} />
              )}
            </Grid.Column>
          </Grid>
          <UserDetailNav
            user={props.user}
            currentUserId={props.currentUserId}
            userGroupName={props.userGroupName}
            canCancelPoints={props.allowCancelPoints}
            revokePoints={props.revokePoints}
            
            loggedInUser={props.loggedInUser}

            loadingUserProfile={props.loadingUserProfile}
            failedUserProfile={props.failedUserProfile}

            loadingStartsCompletes={props.loadingStartsCompletes}
            failedStartsCompletes={props.failedStartsCompletes}

            loadingRaffleEntries={props.loadingRaffleEntries}
            failedRaffleEntries={props.failedRaffleEntries}

            loadingPrizeboothOrders={props.loadingPrizeboothOrders}
            failedPrizeboothOrders={props.failedPrizeboothOrders}

            loadingPointHistory={props.loadingPointHistory}
            failedPointHistory={props.failedPointHistory} />
          <AwardPointsModalContainer />
          <AwardSpinsModalContainer />
          <AwardDaysModalContainer />
          <ChangeEmailModalContainer />
          <ChangePasswordModalContainer />
          <RepairProfileModalContainer />
          <DeleteUserModalContainer user={props.user} undelete={get(props.user, 'deleted')} />
          <RevokePointsModalContainer />
          <ConfirmDeleteUserDataModal />
          <RevokeAmountOfPointsModalContainer/>
        </Segment>
      </Grid.Column>
    </Grid>
  </div>
));

export default UserDetailPage;



// WEBPACK FOOTER //
// ./public/pages/UserManagement/UserDetailPage/UserDetailPage.jsx